import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { ConfigurationService } from '@common/services/configuration.service';
import * as Sentry from '@sentry/angular';

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  private readonly config = inject(ConfigurationService);

  public handleError(error): void {
    const extractedError = this.extractError(error) || 'Handled unknown error';

    if (this.config.get('production')) {
      if (this.isErrorSkipped(error)) return;

      const eventId = Sentry.captureException(extractedError);

      console.error('Unexpected error. Event Id: ' + eventId, extractedError);
    } else {
      // When in development mode, log the error to console for immediate feedback.
      console.error(extractedError);
    }
  }

  private isErrorSkipped(error) {
    return error?.status === HttpStatusCode.Unauthorized;
  }

  private extractError(error) {
    // Try to unwrap zone.js error.
    // https://github.com/angular/angular/blob/master/packages/core/@mbs-ui/util/errors.ts
    if (error?.ngOriginalError) {
      error = error.ngOriginalError;
    }

    // We can handle messages and Error objects directly.
    if (typeof error === 'string' || error instanceof Error) {
      return error;
    }

    // If it's http module error, extract as much information from it as we can.
    if (error instanceof HttpErrorResponse) {
      const nestedError = error.error;

      // The `error` property of http exception can be either an `Error` object, which we can use directly...
      if (nestedError instanceof Error) {
        return nestedError;
      }

      // ... or an`ErrorEvent`, which can provide us with the message but no stack...
      if (nestedError instanceof ErrorEvent) {
        return nestedError.message;
      }

      if (typeof nestedError === 'string') {
        if (error.status === HttpStatusCode.Unauthorized) {
          return `Server returned code 401 - Unauthorized`;
        }

        // ...or the request body itself, which we can use as a message instead.
        return `Server returned code ${error.status} with body "${nestedError}"`;
      }

      // If we don't have any detailed information, fallback to the request message itself.
      return error.message;
    }

    // Skip if there's no error and let the user decide what to do with it.
    return null;
  }
}
